// src/components/SRVOfferDetailModal.jsx
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../axiosSetup";
import dayjs from "dayjs";
import "../css/marketplace-modal.css";
import cfg from "../../config";

function extractServerMessage(err) {
  const s = err?.response?.data?.message;
  if (s) return s;
  const st = err?.response?.status;
  switch (st) {
    case 403: return "Kein Zugriff auf dieses Gegenangebot.";
    case 404: return "Gegenangebot nicht gefunden.";
    case 500: return "Serverfehler beim Laden des Gegenangebots.";
    default:  return err?.message || "Unbekannter Fehler.";
  }
}

// Funktion statt Konstante: auf Modulebene wuerde der Rueckfallwert aus
// config.js eingefroren, bevor AppContext die Serverwerte uebernimmt.
const STATUS_STYLES = () => ({
  ausstehend: {
    bg: "linear-gradient(180deg, #e0f2ff, #dbeafe)",
    border: "#93c5fd",
    text: "#1d4ed8",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
        <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    label: "Ausstehend",
  },
  angenommen_durch_eigentuemer: {
    bg: "linear-gradient(180deg, #ccfbf1, #cffafe)",
    border: "#14b8a6",
    text: "#0f766e",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    label: "Angenommen (Anbieter)",
  },
  genehmigt: {
    bg: "linear-gradient(180deg, #dcfce7, #bbf7d0)",
    border: "#22c55e",
    text: "#166534",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    label: `Genehmigt (${cfg.adminRoleName})`,
  },
  abgelehnt: {
    bg: "linear-gradient(180deg, #fee2e2, #fecaca)",
    border: "#ef4444",
    text: "#991b1b",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    label: "Abgelehnt",
  },
  abgelehnt_durch_eigentuemer: {
    bg: "linear-gradient(180deg, #fee2e2, #fecaca)",
    border: "#ef4444",
    text: "#991b1b",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    label: "Abgelehnt (Anbieter)",
  },
  abgelehnt_durch_rat: {
    bg: "linear-gradient(180deg, #fee2e2, #fecaca)",
    border: "#ef4444",
    text: "#991b1b",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    label: `Abgelehnt (${cfg.adminRoleName})`,
  },
  zurueckgezogen: {
    bg: "linear-gradient(180deg, #ffedd5, #fed7aa)",
    border: "#f59e0b",
    text: "#92400e",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 8v8m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    label: "Zurückgezogen",
  },
  "zurückgezogen": {
    bg: "linear-gradient(180deg, #ffedd5, #fed7aa)",
    border: "#f59e0b",
    text: "#92400e",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 8v8m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    label: "Zurückgezogen",
  },
  abgelaufen: {
    bg: "linear-gradient(180deg, #f3f4f6, #e5e7eb)",
    border: "#9ca3af",
    text: "#374151",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 8v4l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    label: "Abgelaufen",
  },
});

function getStatusChip(statusLike) {
  if (!statusLike) return null;
  const key = String(statusLike).toLowerCase();
  const meta =
    STATUS_STYLES()[key] ||
    { bg: "#eef2ff", border: "#a5b4fc", text: "#3730a3", label: statusLike, icon: null };

  const style = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    background: meta.bg,
    color: meta.text,
    border: `1px solid ${meta.border}`,
  };

  return (
    <span style={style} title={meta.label}>
      <span style={{ display: "inline-flex", alignItems: "center", color: meta.text }}>
        {meta.icon}
      </span>
      <span>{meta.label}</span>
    </span>
  );
}

function ConfirmModal({
  open,
  title,
  text,
  confirmLabel = "OK",
  cancelLabel = "Abbrechen",
  variant = "warning",
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  const bg = variant === "danger" ? "#fee2e2"
          : variant === "success" ? "#ecfdf5"
          : "#fff7ed";
  const border = variant === "danger" ? "1px solid #ef444455"
              : variant === "success" ? "1px solid #10b98155"
              : "1px solid #f59e0b55";

  return createPortal(
    <div
      className="position-fixed top-0 start-0 w-100 h-100"
      style={{ background: "rgba(0,0,0,0.5)", zIndex: 2147483000 }}
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      <div className="container h-100 d-flex align-items-center justify-content-center">
        <div className="card shadow" style={{ width: "min(520px, 94vw)", borderRadius: 12, overflow: "hidden" }}>
          <div className="p-3" style={{ background: bg, borderBottom: border }}>
            <h6 className="m-0">{title}</h6>
          </div>
          <div className="p-3">
            <p className="mb-3" style={{ whiteSpace: "pre-wrap" }}>{text}</p>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-secondary" onClick={onCancel}>
                {cancelLabel}
              </button>
              <button
                type="button"
                className={
                  "btn " + (variant === "danger" ? "btn-danger" :
                            variant === "success" ? "btn-success" : "btn-warning")
                }
                onClick={onConfirm}
              >
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.getElementById("modal-root")
  );
}

const SRVOfferDetailModal = ({ open, onClose, offerId, onAfterAction }) => {
  const [loading, setLoading] = useState(true);
  const [offerData, setOfferData] = useState(null);
  const [banner, setBanner] = useState({ type: null, text: "" });
  const [notFound, setNotFound] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);
  const [councilLoading, setCouncilLoading] = useState(false);

  const [needsRefresh, setNeedsRefresh] = useState(false);

  const [confirmWithdrawOpen, setConfirmWithdrawOpen] = useState(false);
  const [confirmAcceptOpen, setConfirmAcceptOpen] = useState(false);
  const [confirmDeclineOpen, setConfirmDeclineOpen] = useState(false);
  const [confirmCouncilApprove, setConfirmCouncilApprove] = useState(false);
  const [confirmCouncilReject, setConfirmCouncilReject] = useState(false);

  const bannerRef = useRef(null);
  const token = localStorage.getItem("authToken");

  const showBanner = (type, text) => {
    setBanner({ type, text });
    setTimeout(() => {
      bannerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      bannerRef.current?.focus?.();
    }, 50);
  };

  const handleClose = () => {
    try {
      if (needsRefresh) {
        onAfterAction?.();
      }
    } finally {
      onClose?.();
      setNeedsRefresh(false);
    }
  };

  useEffect(() => {
    setNotFound(false);
    setOfferData(null);
    setBanner({ type: null, text: "" });

    if (!open) return;
    if (!offerId) { setLoading(false); return; }

    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/api/swap-marketplace/offers/${offerId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOfferData(res.data);
      } catch (err) {
        if (err?.response?.status === 404) setNotFound(true);
        showBanner("error", extractServerMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, offerId, token]);

  const isOwner       = !!offerData?.permissions?.isOwner;
  const isOfferer     = !!offerData?.permissions?.isOfferer;
  const isZunftrat    = !!offerData?.permissions?.isZunftrat;
  const canOwnerAct   = !!offerData?.permissions?.canOwnerAct;
  const canCouncilAct = !!offerData?.permissions?.canCouncilAct;
  const canOffererWithdraw = isOfferer && offerData?.offer?.status === "ausstehend";

  const hasOwnerAccepted = offerData?.offer?.status === "angenommen_durch_eigentuemer";
  const councilGate = isZunftrat && canCouncilAct && hasOwnerAccepted;

  const doAccept = async () => {
    if (!isOwner || !offerId) return;
    setActionLoading(true);
    try {
      const res = await api.post(
        `/api/swap-marketplace/offers/${offerId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOfferData((prev) => prev ? {
        ...prev,
        offer:   { ...prev.offer,   status: "angenommen_durch_eigentuemer" },
        council: { status: 'ausstehend', reviewerId: null, reviewedAt: null }
      } : prev);
      showBanner("success", res?.data?.message || `Gegenangebot angenommen. Freigabe durch ${cfg.adminRoleName} steht aus.`);
      setNeedsRefresh(true);
    } catch (err) {
      showBanner("error", extractServerMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const doDecline = async () => {
    if (!isOwner || !offerId) return;
    setActionLoading(true);
    try {
      const res = await api.post(
        `/api/swap-marketplace/offers/${offerId}/decline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOfferData((prev) => prev ? { ...prev, offer: { ...prev.offer, status: "abgelehnt" } } : prev);
      showBanner("success", res?.data?.message || "Gegenangebot abgelehnt.");
      setNeedsRefresh(true);
    } catch (err) {
      showBanner("error", extractServerMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const doWithdraw = async () => {
    if (!isOfferer || !offerId) return;
    setActionLoading(true);
    try {
      const res = await api.post(
        `/api/swap-marketplace/offers/${offerId}/withdraw`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOfferData((prev) => prev ? { ...prev, offer: { ...prev.offer, status: "zurueckgezogen" } } : prev);
      showBanner("success", res?.data?.message || "Gegenangebot wurde zurückgezogen.");
      setNeedsRefresh(true);
    } catch (err) {
      showBanner("error", extractServerMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const doCouncilApprove = async () => {
    if (!isZunftrat) { showBanner("error", `Nur ${cfg.adminRoleName} darf freigeben.`); return; }
    if (!canCouncilAct) { showBanner("error", "Zurzeit ist keine Entscheidung möglich."); return; }
    if (!hasOwnerAccepted) { showBanner("error", "Freigabe ist erst nach Annahme durch den Eigentümer möglich."); return; }
    if (!offerId) return;

    setCouncilLoading(true);
    try {
      const res = await api.post(
        `/api/swap-marketplace/offers/${offerId}/council-approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOfferData(prev => prev ? {
        ...prev,
        council: { status: 'genehmigt', reviewerId: null, reviewedAt: new Date().toISOString() }
      } : prev);
      showBanner("success", res?.data?.message || `${cfg.adminRoleName} hat das Gegenangebot genehmigt.`);
      setNeedsRefresh(true);
    } catch (err) {
      showBanner("error", extractServerMessage(err));
    } finally {
      setCouncilLoading(false);
    }
  };

  const doCouncilReject = async () => {
    if (!isZunftrat) { showBanner("error", `Nur ${cfg.adminRoleName} darf ablehnen.`); return; }
    if (!canCouncilAct) { showBanner("error", "Zurzeit ist keine Entscheidung möglich."); return; }
    if (!hasOwnerAccepted) { showBanner("error", "Ablehnung ist erst nach Annahme durch den Eigentümer möglich."); return; }
    if (!offerId) return;

    setCouncilLoading(true);
    try {
      const res = await api.post(
        `/api/swap-marketplace/offers/${offerId}/council-reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOfferData(prev => prev ? {
        ...prev,
        council: { status: 'abgelehnt', reviewerId: null, reviewedAt: new Date().toISOString() }
      } : prev);
      showBanner("success", res?.data?.message || `${cfg.adminRoleName} hat das Gegenangebot abgelehnt.`);
      setNeedsRefresh(true);
    } catch (err) {
      showBanner("error", extractServerMessage(err));
    } finally {
      setCouncilLoading(false);
    }
  };

  const askAccept            = () => setConfirmAcceptOpen(true);
  const cancelAccept         = () => setConfirmAcceptOpen(false);
  const handleAcceptConfirm  = async () => { setConfirmAcceptOpen(false); await doAccept(); };

  const askDecline           = () => setConfirmDeclineOpen(true);
  const cancelDecline        = () => setConfirmDeclineOpen(false);
  const handleDeclineConfirm = async () => { setConfirmDeclineOpen(false); await doDecline(); };

  const askWithdraw          = () => setConfirmWithdrawOpen(true);
  const cancelWithdraw       = () => setConfirmWithdrawOpen(false);
  const handleWithdrawConfirm= async () => { setConfirmWithdrawOpen(false); await doWithdraw(); };

  const askCouncilApprove    = () => setConfirmCouncilApprove(true);
  const cancelCouncilApprove = () => setConfirmCouncilApprove(false);
  const handleCouncilApproveConfirm = async () => {
    setConfirmCouncilApprove(false);
    await doCouncilApprove();
  };

  const askCouncilReject     = () => setConfirmCouncilReject(true);
  const cancelCouncilReject  = () => setConfirmCouncilReject(false);
  const handleCouncilRejectConfirm = async () => {
    setConfirmCouncilReject(false);
    await doCouncilReject();
  };

  if (!open) return null;

  const EmptyState = ({ title, subtitle }) => (
    <div className="d-flex flex-column align-items-center justify-content-center text-center py-5">
      <div
        className="rounded-circle d-flex align-items-center justify-content-center mb-3"
        style={{ width: 64, height: 64, background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb" }}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
          <path d="M12 8h.01M11 12h1v4h1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
      <div className="fw-semibold" style={{ color: "#111" }}>{title}</div>
      {subtitle && <div className="text-muted mt-1" style={{ maxWidth: 520 }}>{subtitle}</div>}
    </div>
  );

  const listingReason = offerData?.listing?.reason;

  const sectionCardStyle = {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 12,
    background: "#fff",
  };

  const markup = (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 marketplace-modal"
      style={{ background: "rgba(0,0,0,0.6)", zIndex: 1050 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="offerDetailTitle"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="container h-100 d-flex align-items-center justify-content-center">
        <div className="card shadow modal-card-contrast" style={{ width: "min(900px, 96vw)", maxHeight: "92vh", overflow: "hidden", borderRadius: 14 }}>

          <div
            className="d-flex align-items-center justify-content-between"
            style={{
              padding: "14px 16px",
              background: "linear-gradient(180deg, #f8fafc, #f1f5f9)",
              borderBottom: "1px solid #e5e7eb"
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 32, height: 32, background: "#eef2ff", color: "#4f46e5", border: "1px solid #e5e7eb" }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M3 7h18M6 12h12M9 17h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h5 id="offerDetailTitle" className="m-0">Gegenangebot</h5>
            </div>

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleClose}
              disabled={actionLoading || councilLoading}
            >
              Schließen
            </button>
          </div>

          {banner.type && (
            <div
              ref={bannerRef}
              tabIndex={-1}
              role="alert"
              aria-live="polite"
              className={`mx-3 mt-3 rounded-3 px-3 py-2 inline-alert inline-alert--${banner.type}`}
            >
              <div className="d-flex align-items-start gap-2">
                <span className="inline-alert__icon" aria-hidden="true" />
                <div className="inline-alert__text">{banner.text}</div>
                <button
                  type="button"
                  className="btn btn-sm btn-link ms-auto inline-alert__close"
                  onClick={() => setBanner({ type: null, text: "" })}
                >
                  Schließen
                </button>
              </div>
            </div>
          )}

          <div style={{ overflowY: "auto" }}>
            <div className="p-3">

              {loading && <p>Lade Gegenangebot…</p>}

              {!loading && !offerId && (
                <EmptyState
                  title="Kein Gegenangebot ausgewählt"
                  subtitle="Bitte gib eine gültige Offer‑ID ein oder öffne dieses Fenster über einen Direktlink aus der Benachrichtigung."
                />
              )}

              {!loading && offerId && notFound && (
                <EmptyState
                  title="Gegenangebot nicht gefunden"
                  subtitle="Das Gegenangebot existiert nicht mehr oder du hast keinen Zugriff."
                />
              )}

              {!loading && offerId && !notFound && !offerData && (
                <EmptyState
                  title="Gegenangebot konnte nicht geladen werden"
                  subtitle="Bitte versuche es später erneut."
                />
              )}

              {!loading && offerData && (
                <>
                  <div className="row g-3">
                    <div className="col-12 col-md-4">
                      <div style={sectionCardStyle}>
                        <div className="text-muted" style={{ fontSize: 12 }}>Erstellt am</div>
                        <div className="fw-semibold mt-1">
                          {dayjs(offerData.offer.createdAt).format("DD.MM.YYYY HH:mm")}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-4">
                      <div style={sectionCardStyle}>
                        <div className="text-muted" style={{ fontSize: 12 }}>Status (Gegenangebot)</div>
                        <div className="mt-1">{getStatusChip(offerData.offer.status)}</div>
                      </div>
                    </div>

                    <div className="col-12 col-md-4">
                      <div style={sectionCardStyle}>
                        <div className="text-muted" style={{ fontSize: 12 }}>Status ({cfg.adminRoleName})</div>
                        <div className="mt-1">
                          {getStatusChip(offerData.council?.status || "—")}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div style={sectionCardStyle}>
                        <div className="text-muted" style={{ fontSize: 12 }}>Angebot von</div>
                        <div className="fw-semibold mt-1">{offerData.offerer?.name || "—"}</div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div style={sectionCardStyle}>
                        <div className="text-muted" style={{ fontSize: 12 }}>Ursprungsangebot</div>
                        <div className="fw-semibold mt-1">{offerData.listing?.assignmentName || "—"}</div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div style={sectionCardStyle}>
                        <div className="fw-semibold">Ursprungsangebot - Arbeitseinsatz</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {offerData.listing?.assignmentName || "—"}
                        </div>

                        {Array.isArray(offerData.listing?.dates) && offerData.listing.dates.length > 0 ? (
                          <ul className="m-0 ps-3 mt-2">
                            {offerData.listing.dates.map((d, i) => (
                              <li key={i}>
                                {dayjs(d.workDate).format("DD.MM.YYYY")} — {String(d.timeFrom).slice(0, 5)}–{String(d.timeTo).slice(0, 5)} Uhr
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="text-muted mt-2">Keine Terminangaben verfügbar.</div>
                        )}
                      </div>
                    </div>

                    {!!listingReason && (
                      <div className="col-12">
                        <div style={{ ...sectionCardStyle, background: "#f8fafc" }}>
                          <div className="text-muted" style={{ fontSize: 12 }}>Nachricht des Ursprungsangebots</div>
                          <div className="mt-2 border rounded p-2 bg-white" style={{ whiteSpace: "pre-wrap" }}>
                            {offerData.listing.reason}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="row g-3 mt-3">
                    <div className="col-12 col-md-6">
                      <div style={sectionCardStyle}>
                        <div className="fw-semibold">Arbeitseinsatz des Gegenangebots</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {offerData.offeredAssignment?.name || "—"}
                        </div>

                        {Array.isArray(offerData.offeredAssignment?.dates) && offerData.offeredAssignment.dates.length > 0 ? (
                          <ul className="m-0 ps-3 mt-2">
                            {offerData.offeredAssignment.dates.map((d, i) => (
                              <li key={i}>
                                {dayjs(d.workDate).format("DD.MM.YYYY")} — {String(d.timeFrom).slice(0, 5)}–{String(d.timeTo).slice(0, 5)} Uhr
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="text-muted mt-2">Keine Terminangaben verfügbar.</div>
                        )}
                      </div>
                    </div>

                    {!!offerData.offer?.message && (
                      <div className="mt-3" style={sectionCardStyle}>
                        <div className="text-muted" style={{ fontSize: 12 }}>Nachricht vom Bieter des Gegenangebots</div>
                        <div className="mt-2 border rounded p-2" style={{ whiteSpace: "pre-wrap" }}>
                          {offerData.offer.message}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-3" style={{ ...sectionCardStyle, background: "#f9fafb" }}>
                    {isOwner && (
                      <>
                        {!canOwnerAct && (
                          <div className="text-muted mb-3">
                            Annahme/Ablehnung zurzeit nicht möglich.
                          </div>
                        )}

                        <div className="d-flex flex-wrap gap-2 align-items-center">
                          <button
                            className="btn btn-success"
                            onClick={askAccept}
                            disabled={!canOwnerAct || actionLoading}
                          >
                            {actionLoading ? "Bitte warten…" : "Annehmen"}
                          </button>

                          <button
                            className="btn btn-danger"
                            onClick={askDecline}
                            disabled={actionLoading || offerData.offer?.status !== "ausstehend"}
                          >
                            {actionLoading ? "Bitte warten…" : "Ablehnen"}
                          </button>
                        </div>
                      </>
                    )}

                    {isOfferer && (
                      <div className="d-flex mt-3">
                        <button
                          className="btn btn-warning ms-auto"
                          onClick={askWithdraw}
                          disabled={!canOffererWithdraw || actionLoading}
                          title={canOffererWithdraw ? "Gegenangebot zurückziehen" : "Zurzeit nicht möglich"}
                        >
                          Zurückziehen
                        </button>
                      </div>
                    )}
                  </div>

                  {isZunftrat && (
                    <div className="mt-3" style={sectionCardStyle}>
                      <div className="fw-semibold mb-2">Freigabe durch {cfg.adminRoleName}</div>

                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-2">
                          <div className="text-muted" style={{ fontSize: 12 }}>Aktueller Status:</div>
                          {getStatusChip(offerData.council?.status || "—")}
                        </div>

                        {councilGate ? (
                          <div className="d-flex flex-wrap gap-2">
                            <button
                              className="btn btn-success"
                              disabled={councilLoading}
                              onClick={askCouncilApprove}
                            >
                              {councilLoading ? "Bitte warten…" : "Freigeben"}
                            </button>
                            <button
                              className="btn btn-danger"
                              disabled={councilLoading}
                              onClick={askCouncilReject}
                            >
                              {councilLoading ? "Bitte warten…" : "Ablehnen"}
                            </button>
                          </div>
                        ) : (
                          <div className="text-muted">
                            {(offerData.council?.status
                                  ? `${cfg.adminRoleName} hat bereits entschieden.`
                                  : "Derzeit keine Aktion möglich.")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirmWithdrawOpen}
        variant="warning"
        title="Gegenangebot zurückziehen?"
        text={"Bist du sicher, dass du dieses Gegenangebot zurückziehen möchtest?\n\nDer Angebotsersteller wird darüber informiert."}
        confirmLabel="Ja, zurückziehen"
        cancelLabel="Abbrechen"
        onConfirm={handleWithdrawConfirm}
        onCancel={cancelWithdraw}
      />

      <ConfirmModal
        open={confirmAcceptOpen}
        variant="success"
        title="Gegenangebot annehmen?"
        text={`Mit der Annahme informierst du den Bieter und startest die Freigabe durch ${cfg.adminRoleName}.\nDieser Schritt kann nicht rückgängig gemacht werden.`}
        confirmLabel="Ja, annehmen"
        cancelLabel="Abbrechen"
        onConfirm={handleAcceptConfirm}
        onCancel={cancelAccept}
      />

      <ConfirmModal
        open={confirmDeclineOpen}
        variant="danger"
        title="Gegenangebot ablehnen?"
        text={"Möchtest du dieses Gegenangebot wirklich ablehnen?\nDer Bieter wird darüber informiert."}
        confirmLabel="Ja, ablehnen"
        cancelLabel="Abbrechen"
        onConfirm={handleDeclineConfirm}
        onCancel={cancelDecline}
      />

      <ConfirmModal
        open={confirmCouncilApprove}
        variant="success"
        title={`${cfg.adminRoleName}: Gegenangebot freigeben?`}
        text={`Möchtest du dieses Gegenangebot freigeben?\nAnbieter und Bieter werden benachrichtigt.`}
        confirmLabel="Ja, freigeben"
        cancelLabel="Abbrechen"
        onConfirm={handleCouncilApproveConfirm}
        onCancel={cancelCouncilApprove}
      />

      <ConfirmModal
        open={confirmCouncilReject}
        variant="danger"
        title={`${cfg.adminRoleName}: Gegenangebot ablehnen?`}
        text={`Möchtest du dieses Gegenangebot ablehnen?\nAnbieter und Bieter werden benachrichtigt.`}
        confirmLabel="Ja, ablehnen"
        cancelLabel="Abbrechen"
        onConfirm={handleCouncilRejectConfirm}
        onCancel={cancelCouncilReject}
      />

    </div>
  );

  const host = document.getElementById("modal-root");
  return host ? createPortal(markup, host) : markup;
};

export default SRVOfferDetailModal;